import React, { useState, useEffect } from 'react';
import {
    Breadcrumb, Layout, Space, Button, Table, message, Spin, Tag, Typography, Modal, Descriptions,
    Row, Col, Select, Input, Form,
} from 'antd'; // Import Card component
import { Card } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import { APPBACKGROUND_COLOR, MAINAPIURL } from '../appsettings';
import { AlertError, AlertSuccess } from '../usableclasses';
import { useRef } from 'react';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { useNavigate } from 'react-router-dom';
import { PDFViewer } from '@react-pdf/renderer'; // Import PDFViewer
import PaymentSlipDocument from './payment-invoice-pdf'; // Import your PDF document component

const { Content } = Layout;
const { Text } = Typography;
const { Option } = Select;

const TellerPaymentsPending = () => {
    const ApiURL = MAINAPIURL;
    const navigator = useNavigate();

    const TOKEN = localStorage.getItem("AUTHCODE");
    const USERID = localStorage.getItem("USERID");

    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [isWalletTopup, setIsWalletTopup] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [tableFilters, setTableFilters] = useState({}); // State to hold table filters
    // State for table search
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [paymentSuccessData, setPaymentSuccessData] = useState(null); // State to hold data for PDF

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            const response = await axios.get(`${ApiURL}/warehouse/allordersforpayment`, { headers });
            setOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching orders:', error);
            AlertError('Failed to fetch orders. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusTag = (status) => {
        const statusUpper = (status || '').toUpperCase();
        let color = 'default';
        switch (statusUpper) {
            case 'SUBMITTED': color = 'cyan'; break;
            case 'UNPAID': color = 'orange'; break;
            case 'PAYMENT FAILED': color = 'volcano'; break;
            case 'PAID': color = 'green'; break;
            case 'PROCESSING': color = 'blue'; break;
            case 'ORDER READY': color = 'purple'; break;
            case 'COMPLETED': color = 'geekblue'; break;
            case 'CANCELLED': color = 'red'; break;
            default: break;
        }
        return <Tag color={color}>{statusUpper || 'N/A'}</Tag>;
    };

    const openPaymentModal = (order) => {
        setSelectedOrder(order);
        form.resetFields();
        setPaymentSuccessData(null); // Reset any previous success data
        setIsModalVisible(true);
    };

    const handleGenerateSlip = async () => {
        try {
            const values = await form.validateFields();

            if (isWalletTopup) {
                if (!values.topUpTotal || Number(values.topUpTotal) <= 0) {
                    return AlertError("Return Amount must be greater than 0");
                }
                if (!values.topUpReason || values.topUpReason.trim() === "") {
                    return AlertError("Reason for Return is required");
                }
            }

            const paymentDetails = `${values.paymentType} ${values.paymentRef}`.trim();

            const payload = {
                orderId: selectedOrder.orderId.toString(),
                addedBy: USERID,
                paymentRef: values.paymentRef,
                paymentType: values.paymentType,
                paidAmount: parseFloat(values.paidAmount),
                paymentDetails: paymentDetails,
                isWalletTopup: isWalletTopup,
                topUpTotal: isWalletTopup ? parseFloat(values.topUpTotal) : 0,
                topUpReason: isWalletTopup ? values.topUpReason.trim() : ""
            };

            setSubmitting(true);
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.post(`${ApiURL}/warehouse/telleraddpayment`, payload, { headers });

            if (res.data === true) {
                AlertSuccess("Payment recorded successfully! Generating slip...");
                setPaymentSuccessData({ order: selectedOrder, payment: values });
                fetchOrders();
            } else {
                AlertError("Failed to record payment. Please check the details and try again.");
                setPaymentSuccessData(null);
            }
        } catch (error) {
            console.error("Error submitting payment:", error);
            AlertError("Failed to record payment. Please try again.");
            setPaymentSuccessData(null);
        } finally {
            setSubmitting(false);
        }
    };

    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex, closeDropdown = true) => {
        confirm({ closeDropdown });
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0] ? [selectedKeys[0]] : null // Store as array for consistency with AntD filters
        }));
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters(); // Clears internal AntD filter state for this column
                            setSearchText(''); // Clear local search text state
                            setSearchedColumn(''); // Clear local searched column state
                            setTableFilters(prev => { // Update the global tableFilters state for this column
                                const newFilters = { ...prev };
                                delete newFilters[dataIndex];
                                return newFilters;
                            });
                            confirm({ closeDropdown: true }); // Close dropdown and apply changes
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
        onFilterDropdownOpenChange: (visible) => { if (visible) { setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (<Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />) : (text),
        filteredValue: tableFilters[dataIndex] || null, // Control filter value
    });

    const columns = [
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        variant="outlined"
                        size="small"
                        color='yellow'
                        onClick={() => openPaymentModal(record)}
                    >
                        Payment
                    </Button>
                    <Button
                        type='primary'
                        variant='solid'
                        size='small'
                        onClick={() => {
                            navigator(`/orders/view/${record.orderId}`); // Navigate to order view
                        }}
                    >
                        View
                    </Button>
                </Space>
            ),
        },
        { title: 'Order No', dataIndex: 'orderRef', key: 'orderRef', ...getColumnSearchProps('orderRef', 'Order No') },
        { title: 'Consultant ID', dataIndex: 'consultantId', key: 'consultantId', ...getColumnSearchProps('consultantId', 'Consultant ID') },
        { title: 'First Name', dataIndex: 'firstName', key: 'firstName', ...getColumnSearchProps('firstName', 'First Name') },
        { title: 'Last Name', dataIndex: 'lastName', key: 'lastName', ...getColumnSearchProps('lastName', 'Last Name') },
        {
            title: 'Order Date/Time',
            dataIndex: 'orderDateTime',
            key: 'orderDateTime',
            render: (text) => text ? new Date(text).toLocaleString() : 'N/A'
        },
        {
            title: 'Platform',
            dataIndex: 'orderPlatform',
            key: 'orderPlatform',
            render: (text) => text?.toUpperCase() || 'N/A',
            ...getColumnSearchProps('orderPlatform', 'Platform')
        },
        {
            title: 'Order Total',
            dataIndex: 'orderCost',
            key: 'orderCost',
            render: (text) => parseFloat(text || 0).toFixed(2),
            ...getColumnSearchProps('orderCost', 'Order Total')
        },
        { title: 'To Be Paid', dataIndex: 'orderToBePaid', key: 'orderToBePaid' },
        { title: 'Ship Method', dataIndex: 'shippedMethod', key: 'shippedMethod', ...getColumnSearchProps('shippedMethod', 'Ship Method') },
        {
            title: 'Is Paid',
            dataIndex: 'isPaid',
            key: 'isPaid',
            render: (isPaid) => getStatusTag(isPaid ? 'PAID' : 'UNPAID'),
            filters: [
                { text: 'Paid', value: true },
                { text: 'Unpaid', value: false },
            ],
            onFilter: (value, record) => record.isPaid === value,
        },
        {
            title: 'Order Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            render: (text) => getStatusTag(text),
            filters: [
                { text: 'Submitted', value: 'Submitted' },
                { text: 'Unpaid', value: 'Unpaid' },
                { text: 'Paid', value: 'Paid' },
                { text: 'Processing', value: 'Processing' },
                { text: 'Order Ready', value: 'Order Ready' },
                { text: 'Completed', value: 'Completed' },
                { text: 'Cancelled', value: 'Cancelled' },
            ],
            onFilter: (value, record) => record.orderStatus === value,
        }
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        setTableFilters(filters);
        // If you have server-side pagination/sorting/filtering, you would trigger data fetch here
    };

    const handleClearAllTableFilters = () => {
        setTableFilters({}); // Clear all filters
        setSearchText(''); // Clear any active search text
        setSearchedColumn(''); // Clear any active searched column
    };

    return (
        <Spin spinning={loading} tip="Loading...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Teller</Breadcrumb.Item>
                        <Breadcrumb.Item>Payments Pending</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col xs={24} sm={12} md={12}>
                                <Space wrap>
                                    <Tag color="blue">Total Pending Payments: {orders.length}</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} sm={12} md={12} style={{ textAlign: 'right' }}>
                                <Space wrap>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearAllTableFilters}
                                        style={{ marginLeft: 8 }}
                                    >
                                        Clear All Table Filters
                                    </Button>
                                    <Button onClick={fetchOrders}>Refresh List</Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Table
                        dataSource={orders}
                        columns={columns}
                        rowKey="orderId"
                        scroll={{ x: 'max-content' }} // Make table horizontally scrollable
                        pagination={{
                            pageSize: 10, // Default page size
                            showSizeChanger: false, // Hide page size changer
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            // You might want to add onChange if you plan to implement server-side pagination
                        }}
                        locale={{
                            emptyText: loading ? 'Fetching orders...' : (<p>No orders!</p>)
                        }}
                    />

                    {/* Modal */}
                    <Modal
                        title={paymentSuccessData ? "Payment Slip" : "Order Payment"}
                        open={isModalVisible}
                        onCancel={() => {
                            setIsModalVisible(false);
                            setPaymentSuccessData(null);
                            setIsWalletTopup(false);
                        }}
                        width={700}
                        footer={paymentSuccessData ? [
                            <Button key="close" onClick={() => {
                                setIsModalVisible(false);
                                setPaymentSuccessData(null);
                                setIsWalletTopup(false);
                            }}>
                                Cancel
                            </Button>
                        ] : [
                            <Button key="cancel" onClick={() => {
                                setIsModalVisible(false);
                                setPaymentSuccessData(null);
                                setIsWalletTopup(false);
                            }} danger>
                                Cancel
                            </Button>,
                            <Button key="submit" type="primary" loading={submitting} onClick={handleGenerateSlip}>
                                Generate Slip
                            </Button>
                        ]}
                    >
                        {selectedOrder && (
                            <>
                                {paymentSuccessData ? (
                                    <PDFViewer width="100%" height="500px">
                                        <PaymentSlipDocument
                                            order={paymentSuccessData.order}
                                            payment={paymentSuccessData.payment}
                                        />
                                    </PDFViewer>
                                ) : (
                                    <>
                                        <Descriptions bordered column={2} size="small">
                                            <Descriptions.Item label="Order No">{selectedOrder.orderRef}</Descriptions.Item>
                                            <Descriptions.Item label="Consultant ID">{selectedOrder.consultantId}</Descriptions.Item>
                                            <Descriptions.Item label="First Name">{selectedOrder.firstName}</Descriptions.Item>
                                            <Descriptions.Item label="Last Name">{selectedOrder.lastName}</Descriptions.Item>
                                            <Descriptions.Item label="Date/Time">{new Date(selectedOrder.orderDateTime).toLocaleString()}</Descriptions.Item>
                                            <Descriptions.Item label="Order Total">{parseFloat(selectedOrder.orderCost).toFixed(2)}</Descriptions.Item>
                                            <Descriptions.Item label="Total Paid">{parseFloat(selectedOrder.orderPaid).toFixed(2)}</Descriptions.Item>
                                            <Descriptions.Item label="To Be Paid">{parseFloat(selectedOrder.orderToBePaid).toFixed(2)}</Descriptions.Item>
                                        </Descriptions>

                                        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="paymentType"
                                                        label="Payment Type"
                                                        rules={[{ required: true, message: 'Please select payment type' }]}
                                                    >
                                                        <Select placeholder="Select Payment Type">
                                                            <Option value="CASH">CASH</Option>
                                                            <Option value="POS">POS</Option>
                                                            <Option value="TRANSFER">TRANSFER</Option>
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="paidAmount"
                                                        label="Paid Amount"
                                                        rules={[{ required: true, message: 'Please enter payment amount' },
                                                        { pattern: new RegExp(/^(?!0\.0*$)([1-9]\d*|0)(\.\d+)?$/), message: 'Please enter a valid number.', }]}
                                                    >
                                                        <Input placeholder="Enter Amount" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="paymentRef"
                                                        label="Payment Reference"
                                                        rules={[{ required: true, message: 'Please enter payment reference' }]}
                                                    >
                                                        <Input placeholder="Enter Reference" />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Payment Details"
                                                shouldUpdate={(prev, curr) =>
                                                    prev.paymentType !== curr.paymentType || prev.paymentRef !== curr.paymentRef
                                                }
                                            >
                                                {({ getFieldValue }) => (
                                                    <Input
                                                        readOnly
                                                        value={`${getFieldValue('paymentType') || ''} ${getFieldValue('paymentRef') || ''}`.trim()}
                                                    />
                                                )}
                                            </Form.Item>

                                            <Form.Item label="">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={isWalletTopup}
                                                        onChange={(e) => setIsWalletTopup(e.target.checked)}
                                                    />{" "}
                                                    Cash Return to Wallet
                                                </label>
                                            </Form.Item>

                                            {isWalletTopup && (
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="topUpTotal"
                                                            label="Return Amount"
                                                            rules={[{ required: true, message: 'Enter return amount' }]}
                                                        >
                                                            <Input type="number" min={0} step="0.01" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="topUpReason"
                                                            label="Reason for Return"
                                                            rules={[{ required: true, message: 'Enter return reason' }]}
                                                        >
                                                            <Input placeholder="Reason for wallet top-up" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            )}
                                        </Form>
                                    </>
                                )}
                            </>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default TellerPaymentsPending;
